import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { allocateMovingAverageOutboundFen } from "./purchase-return.js";

const NonNegativeSafeIntegerSchema = Type.Integer({
  minimum: 0,
  maximum: Number.MAX_SAFE_INTEGER,
});
const InventoryLossIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]*$",
});
const InventoryLossNumberSchema = Type.String({ pattern: "^BS-[0-9]{8}-[0-9A-F]{12}$" });
const InventoryLossDateTimeSchema = Type.String({
  pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(?:\\.[0-9]{3})?Z$",
});
const InventoryLossQuantitySchema = Type.Integer({ minimum: 1, maximum: 1_000_000 });

/**
 * Gateway-authoritative inventory and valuation facts frozen before a loss
 * request is submitted to the shared approval center.
 */
export const InventoryLossCaptureSchema = Type.Object(
  {
    captureVersion: Type.Literal(1),
    lossNumber: InventoryLossNumberSchema,
    quantity: InventoryLossQuantitySchema,
    quantityBefore: InventoryLossQuantitySchema,
    quantityAfter: NonNegativeSafeIntegerSchema,
    activeReservedQuantity: NonNegativeSafeIntegerSchema,
    inventoryVersionBefore: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    inventoryValueBeforeFen: NonNegativeSafeIntegerSchema,
    inventoryValueDecreaseFen: NonNegativeSafeIntegerSchema,
    inventoryValueAfterFen: NonNegativeSafeIntegerSchema,
    inventoryValuationVersionBefore: Type.Integer({
      minimum: 1,
      maximum: Number.MAX_SAFE_INTEGER,
    }),
    capturedAt: InventoryLossDateTimeSchema,
  },
  { additionalProperties: false },
);
export type InventoryLossCapture = Static<typeof InventoryLossCaptureSchema>;

export const InventoryLossAppliedFactSchema = Type.Object(
  {
    factVersion: Type.Literal(1),
    approvalId: InventoryLossIdSchema,
    lossNumber: InventoryLossNumberSchema,
    movementId: InventoryLossIdSchema,
    skuId: Type.String({ minLength: 1, maxLength: 64 }),
    productVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    productName: Type.String({ minLength: 1, maxLength: 80 }),
    unit: Type.String({ minLength: 1, maxLength: 16 }),
    capture: InventoryLossCaptureSchema,
    executionInventoryMovementSequence: Type.Integer({
      minimum: 1,
      maximum: Number.MAX_SAFE_INTEGER,
    }),
    quantity: InventoryLossQuantitySchema,
    quantityBefore: InventoryLossQuantitySchema,
    quantityAfter: NonNegativeSafeIntegerSchema,
    inventoryVersionBefore: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    inventoryVersionAfter: Type.Integer({ minimum: 2, maximum: Number.MAX_SAFE_INTEGER }),
    inventoryValueBeforeFen: NonNegativeSafeIntegerSchema,
    inventoryValueDecreaseFen: NonNegativeSafeIntegerSchema,
    inventoryValueAfterFen: NonNegativeSafeIntegerSchema,
    inventoryValuationVersionBefore: Type.Integer({
      minimum: 1,
      maximum: Number.MAX_SAFE_INTEGER,
    }),
    inventoryValuationVersionAfter: Type.Integer({
      minimum: 2,
      maximum: Number.MAX_SAFE_INTEGER,
    }),
    actorId: InventoryLossIdSchema,
    authorizerId: InventoryLossIdSchema,
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    occurredAt: InventoryLossDateTimeSchema,
  },
  { additionalProperties: false },
);
export type InventoryLossAppliedFact = Static<typeof InventoryLossAppliedFactSchema>;

export const InventoryLossBusinessEventPayloadSchema = Type.Object(
  { inventoryLoss: InventoryLossAppliedFactSchema },
  { additionalProperties: false },
);
export type InventoryLossBusinessEventPayload = Static<
  typeof InventoryLossBusinessEventPayloadSchema
>;

export function isInventoryLossCapture(value: unknown): value is InventoryLossCapture {
  if (!Value.Check(InventoryLossCaptureSchema, value)) return false;
  let expectedDecreaseFen: number;
  try {
    expectedDecreaseFen = allocateMovingAverageOutboundFen(
      value.inventoryValueBeforeFen,
      value.quantityBefore,
      value.quantity,
    );
  } catch {
    return false;
  }
  return (
    value.quantityBefore - value.quantity === value.quantityAfter &&
    value.quantityAfter >= value.activeReservedQuantity &&
    value.inventoryVersionBefore === value.inventoryValuationVersionBefore &&
    value.inventoryValueDecreaseFen === expectedDecreaseFen &&
    value.inventoryValueBeforeFen - expectedDecreaseFen === value.inventoryValueAfterFen &&
    (value.quantityAfter !== 0 || value.inventoryValueAfterFen === 0)
  );
}

export function isInventoryLossAppliedFact(value: unknown): value is InventoryLossAppliedFact {
  if (!Value.Check(InventoryLossAppliedFactSchema, value)) return false;
  return (
    isInventoryLossCapture(value.capture) &&
    value.actorId !== value.authorizerId &&
    value.lossNumber === value.capture.lossNumber &&
    value.quantity === value.capture.quantity &&
    value.quantityBefore === value.capture.quantityBefore &&
    value.quantityAfter === value.capture.quantityAfter &&
    value.inventoryVersionBefore === value.capture.inventoryVersionBefore &&
    value.inventoryVersionAfter === value.inventoryVersionBefore + 1 &&
    value.inventoryValueBeforeFen === value.capture.inventoryValueBeforeFen &&
    value.inventoryValueDecreaseFen === value.capture.inventoryValueDecreaseFen &&
    value.inventoryValueAfterFen === value.capture.inventoryValueAfterFen &&
    value.inventoryValuationVersionBefore === value.capture.inventoryValuationVersionBefore &&
    value.inventoryValuationVersionAfter === value.inventoryValuationVersionBefore + 1
  );
}
